import { useEffect, useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { PANEL } from "@/constants/testIds";
import { fetchMatches, fetchStats } from "@/lib/api";
import { X } from "lucide-react";

export default function GenericPanel({ title, subtitle, testId, empty, showStats = false }) {
    const goto = useGameStore((s) => s.goto);
    const [stats, setStats] = useState(null);
    const [matches, setMatches] = useState([]);

    useEffect(() => {
        if (!showStats) return;
        fetchStats().then(setStats).catch(() => {});
        fetchMatches(15).then(setMatches).catch(() => {});
    }, [showStats]);

    return (
        <div data-testid={testId} className="fixed inset-0 bg-black flex flex-col axis-fade-in overflow-hidden">
            <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
                <div>
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-cyan-300/80">{subtitle}</div>
                    <div className="axis-heading text-2xl tracking-[0.4em] text-white">{title.toUpperCase()}</div>
                </div>
                <button
                    data-testid={PANEL.close}
                    onClick={() => goto(SCREEN.MENU)}
                    className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
                >
                    <X size={14} /> Close
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-10 py-10">
                {showStats && stats ? (
                    <div className="max-w-5xl mx-auto space-y-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <BigStat label="Matches" value={stats.profile.matches_played} />
                            <BigStat label="Wins" value={stats.profile.wins} color="#00ff66" />
                            <BigStat label="K/D" value={stats.kd} accent />
                            <BigStat label="Win Rate" value={`${stats.win_rate}%`} color="#ffb300" />
                            <BigStat label="Kills" value={stats.profile.kills} />
                            <BigStat label="Deaths" value={stats.profile.deaths} color="#ff2a2a" />
                            <BigStat label="Level" value={stats.profile.level} accent />
                            <BigStat label="XP" value={stats.profile.xp} />
                        </div>

                        <div>
                            <div className="axis-mono text-[10px] tracking-[0.5em] text-white/50 mb-3">MATCH HISTORY</div>
                            {matches.length === 0 ? (
                                <EmptyState text="No matches recorded yet." />
                            ) : (
                                <div className="space-y-1">
                                    {matches.map((m) => (
                                        <div key={m.id} className="grid grid-cols-6 gap-3 items-center axis-cut-sm border border-white/10 bg-white/[0.02] px-4 py-2.5 text-xs">
                                            <span className={`axis-heading tracking-widest ${m.won ? "text-cyan-300" : "text-red-400"}`}>
                                                {m.won ? "VICTORY" : "DEFEAT"}
                                            </span>
                                            <span className="axis-mono text-white/70">{m.map}</span>
                                            <span className="axis-mono text-white/70">{m.team_size}</span>
                                            <span className="axis-mono text-white/70">{m.difficulty}</span>
                                            <span className="axis-hud text-lg text-white text-right">{m.kills}K</span>
                                            <span className="axis-hud text-lg text-red-400 text-right">{m.deaths}D</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <EmptyState text={empty} />
                )}
            </div>
        </div>
    );
}

function BigStat({ label, value, color, accent }) {
    return (
        <div className="axis-glass axis-cut p-5">
            <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50 mb-2">{label}</div>
            <div
                className={`axis-hud text-5xl ${accent ? "text-cyan-300" : "text-white"}`}
                style={color ? { color } : {}}
            >
                {value}
            </div>
        </div>
    );
}

function EmptyState({ text }) {
    return (
        <div className="h-full flex flex-col items-center justify-center gap-4 text-center py-16">
            <div className="w-16 h-16 axis-cut border border-cyan-400/30 flex items-center justify-center">
                <div className="axis-heading text-2xl axis-metallic">AX</div>
            </div>
            <div className="axis-mono text-xs tracking-[0.3em] text-white/50 max-w-lg">
                {text}
            </div>
            <div className="axis-mono text-[10px] tracking-[0.4em] text-amber-300/70">
                BETA · MORE CONTENT SOON
            </div>
        </div>
    );
}
